import { useMemo } from 'react';
import { useExperienceEvents } from '@/features/events/hooks/useExperienceEvents';
import { hasInstanceInWindow } from '@/features/events/lib/eventVisibility.lib';
import type { DateTimeRange } from '@/features/events/lib/range.lib';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import type { PageExperience } from '@/features/experiences/types';
import { usePlannerViewRange } from '@/features/planner/hooks/usePlannerViewRange';
import { useVisibleExperienceEvents } from '@/features/planner/hooks/useShowHiddenEvents';

type Args = {
  experience: PageExperience;
  range: DateTimeRange | undefined;
};

// Which categories the Key lists: every named one, plus unnamed ones that events
// in view carry. A planner keeps eight category slots whether or not anything uses
// them, so listing unnamed slots wholesale fills the Key with swatches for colors
// that appear nowhere.
//
// The query is the view's own with the categories facet dropped. Dropping it keeps
// the chips stable: read off the filtered events, a chip would vanish the moment
// you filtered it out, and a chip you can't see is one you can't switch back on.
// Deriving the rest from context.eventFilters rather than rebuilding the range
// keeps the react-query key identical to the view's, so the two dedupe.
//
// The range narrows the fetched events rather than the query, which is what
// preserves that dedup — only the edit provider bounds its own fetch by ?range=.
export const useKeyCategories = ({
  experience,
  range,
}: Args): PageExperience['categories'] => {
  const { eventFilters, isPublishedView, isShowingHiddenEvents } =
    usePageExperienceContext();
  // The bar mounts above the child's provider, so this comes from the store.
  const { contentWindow } = usePlannerViewRange();

  const usageFilters = useMemo(() => {
    if (!eventFilters) {
      return undefined;
    }
    const withoutCategories = { ...eventFilters };
    delete withoutCategories.categories;
    return withoutCategories;
  }, [eventFilters]);

  const { events: fetchedEvents } = useExperienceEvents({
    experience,
    filters: usageFilters,
    published: isPublishedView,
  });
  const visibleEvents = useVisibleExperienceEvents(experience, fetchedEvents);

  // The rendered window, not the canvas `range` is: a view showing Sep 10-20 lays
  // out all of September, and a colour used only on Sep 22 is not on the page.
  const scope = useMemo(
    () =>
      contentWindow?.start || contentWindow?.end
        ? contentWindow
        : range
          ? { start: range.startDate, end: range.endDate }
          : undefined,
    [contentWindow, range]
  );

  const usedCategoryIds = useMemo(() => {
    const used = new Set<string>();
    for (const event of visibleEvents) {
      const id = event.metadata?.category?.id;
      // Skipping a colour already found is what keeps this cheap: the test below
      // expands recurrence rules, and there are at most eight colours.
      if (!id || used.has(id)) {
        continue;
      }
      // Per occurrence: a Monday-only series overlaps a Tuesday-to-Wednesday
      // window without rendering in it.
      if (
        !scope ||
        hasInstanceInWindow(event, scope, {
          showHiddenEvents: isShowingHiddenEvents,
        })
      ) {
        used.add(id);
      }
    }
    return used;
  }, [visibleEvents, scope, isShowingHiddenEvents]);

  return useMemo(
    () =>
      experience.categories.filter(
        (category) => category.text || usedCategoryIds.has(category.id)
      ),
    [experience.categories, usedCategoryIds]
  );
};
